import React, { useMemo, useState, useEffect } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import JSZip from 'jszip';
import { useIntl } from 'react-intl';
import { ChangeProjectDropDown } from '../../../../../GuestHomepage/view/frontend/src/ChangeProjectDropDown';
import {
  cn,
  DialogOverlay,
  Button,
  Text,
  ArrowPushDownIcon,
  CrossIcon,
  Checkbox,
  createOrder,
  getCreateOrderPayload,
  getBearer,
  fetchAttachments,
  AttachmentsResponse,
  prettifyBytes,
  LoadingIcon,
  Dialog,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useBreakpoint,
  fetchProductBySku,
  getShouldTriggerProjectConfirmationDialog,
  ProductResponse,
} from '../../../../../UILibrary';
import { definedTranslation } from '../i18n';
import { getMaterialsByKeyList } from '../utils';
import { previewFilesAnalyticsEvents } from './RequestQuote/analytics';

interface OpenPreviewFilesDialogCustomEvent {
  selectedFileIdx?: number;
  selectedFileId?: number;
  productId?: string; // The ID of the product.
  productSku?: string; // The SKU of the product to fetch attachments for. Must be parent sku if configurable!
  thumbnailProduct?: {
    productName: string;
    brandName: string;
    productUrl: string;
    productImage: string;
  };
}

interface ProductDetails {
  thumbnailImageUrl: string;
  categories: string[];
  name: string;
  manufacturer: string;
  url: string;
  productSku: string | null;
}

export const PreviewFiles = () => {
  const intl = useIntl();
  const { projects } = useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  // Event data
  const [productSku, setProductSku] = useState<string | null>(null);

  // Local data
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [filesResponse, setFilesResponse] =
    useState<AttachmentsResponse | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSelectingFiles, setIsSelectingFiles] = useState(false);
  const [product, setProduct] = useState<ProductResponse | null>(null);

  // Product details
  const [productDetails, setProductDetails] = useState<ProductDetails | null>(
    null,
  );

  // File preview
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null,
  );

  // PDF viewing
  const [zoomLevel, setZoomLevel] = useState<number>(1.0);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  // Single-file select & Preview
  const selectedFile = useMemo(() => {
    if (filesResponse?.items && selectedFileIndex !== null) {
      return filesResponse.items[selectedFileIndex];
    }
    return null;
  }, [filesResponse, selectedFileIndex]);

  // Multi-file select
  const [checkedFileMap, setCheckedFileMap] = useState<Record<number, any>>({});
  const totalFilesChecked = useMemo(() => {
    return Object.values(checkedFileMap).filter((v) => v === true).length;
  }, [checkedFileMap]);
  const selectedFiles = useMemo(() => {
    if (filesResponse && filesResponse.items?.length >= 1) {
      return filesResponse?.items.filter((item, idx) => {
        return checkedFileMap[idx];
      });
    } else {
      return [];
    }
  }, [filesResponse, checkedFileMap]);

  const { isSmall } = useBreakpoint();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fileIdParam = parseInt(urlParams.get('fileId') ?? '', 10);

    if (!isNaN(fileIdParam)) {
      handleOpen({ detail: { selectedFileId: fileIdParam } });
    }
  }, [window.location.href]);

  const openPreviewFilesDialog = async (event: {
    detail: OpenPreviewFilesDialogCustomEvent;
  }) => {
    const selectedFileIdx = event.detail?.selectedFileIdx || 0;
    const selectedFileId = event.detail?.selectedFileId;

    // Set events (product id and sku)
    const productId =
      event?.detail?.productId ??
      window.productAttributeJson?.product_id?.value ??
      null;

    const productSku =
      event?.detail?.productSku ??
      window.productAttributeJson?.sku?.value ??
      null;
    setProductSku(productSku);

    if (typeof selectedFileIdx === 'number') {
      setSelectedFileIndex(selectedFileIdx);
    }

    setIsOpen(true);
    const bearerToken = getBearer();
    setIsFetchingFiles(true);

    fetchProductBySku(productSku).then((response) => {
      setProduct(response);
    });

    try {
      const [attachmentsResponse, materialInfoResponse] = await Promise.all([
        fetchAttachments(bearerToken, productSku),
        // TODO: refactor without type assertion, or add comment
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        getMaterialsByKeyList([productId as string]),
      ]);

      if (
        !!materialInfoResponse?.results &&
        materialInfoResponse?.results?.length > 0
      ) {
        const product = materialInfoResponse.results[0];
        const thumbnailImageUrl = product.thumbnailImageUrl;
        const name = product.name;
        const manufacturer = product.manufacturer;
        const url = product.url;

        setProductDetails({
          thumbnailImageUrl,
          categories: product.categories,
          name,
          manufacturer,
          url,
          productSku,
        });
      } else if (!!event.detail?.thumbnailProduct) {
        const thumbnailImageUrl = event.detail?.thumbnailProduct?.productImage;
        const name = event.detail?.thumbnailProduct?.productName;
        const manufacturer = event.detail?.thumbnailProduct?.brandName;
        const url = event.detail?.thumbnailProduct?.productUrl;

        setProductDetails({
          thumbnailImageUrl,
          categories: [],
          name,
          manufacturer,
          url,
          productSku,
        });
      } else {
        setProductDetails(null);
      }

      setFilesResponse(attachmentsResponse);

      if (!selectedFileIdx && !!selectedFileId) {
        const foundIndexFromFileId =
          attachmentsResponse.items.findIndex((file) => {
            return file.attachment_id === selectedFileId;
          }) || 0;

        // set first file if not found
        setSelectedFileIndex(foundIndexFromFileId);
      } else if (!!selectedFileIdx && !!selectedFileId) {
        setSelectedFileIndex(0);
      }
    } finally {
      setIsFetchingFiles(false);
    }

    document.body.style.overflow = 'hidden';
    setZoomLevel(1.0);
  };

  const handleOpen = async (event: {
    detail: OpenPreviewFilesDialogCustomEvent;
  }) => {
    const currentProjectId = currentProject?.externalId?.toString() ?? '';
    const shouldTriggerProjectConfirmationDialog =
      getShouldTriggerProjectConfirmationDialog();

    if (shouldTriggerProjectConfirmationDialog) {
      window.dispatchEvent(
        new CustomEvent('open-project-selection-dialog', {
          detail: {
            currentProjectId,
            analyticsEventLocation: 'Get pricing',
            onCurrentProjectSave: () => {
              openPreviewFilesDialog(event);
            },
          },
        }),
      );
    } else {
      openPreviewFilesDialog(event);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const listener = (
      event: CustomEvent<OpenPreviewFilesDialogCustomEvent>,
    ) => {
      handleOpen(event);
    };

    window.addEventListener('open-preview-files-dialog', listener);

    return () => {
      window.removeEventListener('open-preview-files-dialog', listener);
    };
  }, []);

  const onFileClicked = (idx: number) => {
    if (selectedFileIndex === idx) {
      // Uncheck
      setSelectedFileIndex(null);
    } else {
      setSelectedFileIndex(idx);
      setZoomLevel(1.0);
    }
  };

  const onSelectFilesClicked = () => {
    setIsSelectingFiles(true);
  };

  const onCancelSelectFilesClicked = () => {
    setCheckedFileMap({});
    setIsSelectingFiles(false);
  };

  const resetState = () => {
    setIsSelectingFiles(false);
    setCheckedFileMap({});
    setSelectedFileIndex(null);
    setZoomLevel(1.0);
  };

  useEffect(() => {
    // When the screen is resized to small, multi-select is not present so should be reset
    onCancelSelectFilesClicked();
  }, [isSmall]);

  const onDownloadFileClicked = async () => {
    if (isSelectingFiles && selectedFiles?.length > 0) {
      // Multi-file download
      const zip = new JSZip();

      // Add each selected file to the ZIP
      for (const file of selectedFiles) {
        if (file?.filename) {
          const response = await fetch(file.filename, {
            mode: 'cors',
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${file.filename}`);
          }

          const blob = await response.blob();
          zip.file(file.name, blob);
        }
      }

      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download the ZIP file
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'files.zip'; // Set the ZIP filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      resetState();
      handleClose();
    } else {
      // Single file download
      if (selectedFile?.filename) {
        const response = await fetch(selectedFile.filename, {
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        // Download invidual file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = selectedFile?.name;
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        resetState();
        handleClose();
      }
    }

    const taxonomyValue =
      product?.custom_attributes?.taxonomy_us ??
      window.productAttributeJson?.taxonomy_us?.value;
    const taxonomies = taxonomyValue?.split('.') || [];

    const location = window.location.href?.includes('/digitalcatalogdownloads')
      ? 'digital_sampling_download_history'
      : 'digital_sampling_pdp';

    previewFilesAnalyticsEvents.fileDownloaded(
      {
        downloaded_item_count: selectedFiles?.length || 1,
        event_project_name: currentProject?.title,
        event_project_id: currentProject?.externalId?.toString(),
        event_item_id: productSku ?? window.productAttributeJson?.sku?.value,
        event_item_name:
          product?.name ?? window.productAttributeJson?.name?.value,
        event_item_brand:
          product?.custom_attributes?.manufacturer ??
          window.productAttributeJson?.manufacturer?.value,
        event_item_category: taxonomies?.[0],
        event_item_category2: taxonomies?.[0],
        event_item_category3: taxonomies?.[2],
        event_item_variant: window.productAttributeJson?.color?.value,
      },
      location,
    );

    if (!!productDetails?.productSku) {
      if (!currentProject) {
        // Open the create project modal if no project is selected
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: 'Digital sampling',
            },
          }),
        );

        return;
      }

      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: currentProject?.externalId,
          successCallback: async (project: ProjectModel) => {
            const orderData = getCreateOrderPayload(
              productDetails.productSku!,
              project,
              'Download',
              {
                attachmentPimId:
                  selectedFile?.pim_id ?? selectedFiles[0]?.pim_id,
                attachmentId:
                  selectedFile?.attachment_id ??
                  selectedFiles[0]?.attachment_id,
              },
            );

            createOrder(orderData!);
          },
        });

        return;
      }

      const orderData = getCreateOrderPayload(
        productDetails?.productSku,
        currentProject,
        'Download',
        {
          attachmentPimId: selectedFile?.pim_id ?? selectedFiles[0]?.pim_id,
          attachmentId:
            selectedFile?.attachment_id ?? selectedFiles[0]?.attachment_id,
        },
      );

      createOrder(orderData!);
    }
  };

  const handleLearnMoreClick = () => {
    if (!!productDetails?.url) {
      window.location.href = productDetails?.url;
    }
  };

  const PdfContent = useMemo(() => {
    if (selectedFileIndex === null) {
      return (
        <Text className="text-center text-[#86868B] max-w-[350px]">
          {intl?.formatMessage(
            definedTranslation.previewFiles.unselectedFileLabel,
          )}
        </Text>
      );
    }
    return (
      <div className="relative w-full h-full m-0 p-0 text-[16px] text-center overflow-auto">
        <div
          style={{
            position: 'absolute',
            top: '0px',
            height: '60px',
            zIndex: 999,
            backgroundColor: '#f5f5f7',
            width: '100%',
          }}
        />
        <iframe
          ref={iframeRef}
          className="border-0"
          src={selectedFile?.filename}
          width="100%"
          height="100%"
        />
      </div>
    );
  }, [selectedFile, selectedFileIndex, zoomLevel]);

  const ProductDetailsSection = () => {
    if (!productDetails?.thumbnailImageUrl || !productDetails?.name) {
      return null;
    }

    return (
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {productDetails.thumbnailImageUrl && (
              <img
                style={{ width: '72px', height: '72px' }}
                src={productDetails.thumbnailImageUrl}
                alt={productDetails.name || 'Product thumbnail'}
                className="w-full h-auto object-cover rounded-md w-18 h-18"
              />
            )}
          </div>

          <div className="flex-grow">
            {/* Product name */}
            {productDetails.manufacturer && (
              <Text
                variant="Caption1"
                className="text-[#4D4E58] font-bold leading-[20px]"
              >
                {productDetails.manufacturer}
              </Text>
            )}

            {/* Manufacturer */}
            {productDetails.name && (
              <Text
                variant="Caption2"
                className="text-[#4D4E58] leading-[20px]"
              >
                {productDetails.name}
              </Text>
            )}

            <Button
              className="h-[28px] w-[94px] text-[13px] mt-[4px]"
              variant="secondary"
              onClick={handleLearnMoreClick}
            >
              {intl?.formatMessage(
                definedTranslation.previewFiles.learnMoreBtn,
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DialogOverlay isOpen={isOpen} handleClose={handleClose}>
      <Dialog isOpen={isOpen} maxHeight={840} maxWidth={1220}>
        <div className="flex flex-col gap-2 py-4 px-4 sm:px-6">
          <div className="flex justify-between items-center ">
            <Text variant="H2" md="Title3">
              {intl?.formatMessage(
                definedTranslation.previewFiles.downloadFilesLabel,
              )}
            </Text>
            <div
              className="flex items-center justify-center w-8 h-8 cursor-pointer"
              onClick={handleClose}
            >
              <CrossIcon className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
          </div>
          {/* Mobile - File selector */}
          <Select
            value={selectedFileIndex?.toString() ?? ''}
            onChange={(val) => {
              setSelectedFileIndex(parseInt(val));
              setZoomLevel(1.0); // Reset zoom when changing files
            }}
            direction="bottom-start"
            options={filesResponse?.items?.map((file, idx) => ({
              value: idx.toString(),
              label: file.name,
            }))}
          >
            <SelectTrigger className="w-full flex sm:hidden">
              <SelectValue
                placeholder={intl?.formatMessage(
                  definedTranslation.previewFiles.fileSelectorPlaceholder,
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {filesResponse?.items?.map((file, idx) => (
                <SelectItem key={file.name} value={idx.toString()}>
                  {file.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mobile */}
        <div className="flex sm:hidden overflow-hidden border-t border-t-[#F2F2F2] flex-1">
          <div className="flex flex-1 items-center justify-center px-6 pb-10 bg-[#F5F5F7] rounded-b-2xl">
            {PdfContent}
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden sm:flex overflow-hidden border-t border-t-[#F2F2F2] flex-1">
          <div
            className="overflow-hidden flex flex-col h-full"
            style={{ width: '370px', flexShrink: 0 }}
          >
            {productDetails?.thumbnailImageUrl && <ProductDetailsSection />}

            {productDetails?.thumbnailImageUrl && (
              <hr className="border-t border-[#F2F2F2] mx-6" />
            )}

            <div className="flex-1 overflow-auto pt-6 px-6">
              {!isFetchingFiles ? (
                <div className="flex flex-col gap-3">
                  {filesResponse?.items?.map((file, idx) => (
                    <div
                      key={file.name + idx}
                      className={cn(
                        'cursor-pointer flex items-start gap-2 w-full px-3 py-1 rounded-md',
                        {
                          'bg-[#F5F5F7]': selectedFileIndex === idx,
                        },
                      )}
                      onClick={() => onFileClicked(idx)}
                    >
                      {isSelectingFiles && (
                        <Checkbox
                          checked={!!checkedFileMap[idx]}
                          onClick={() => {
                            const fileMap = Object.assign({}, checkedFileMap);
                            fileMap[idx] = !fileMap[idx];
                            setCheckedFileMap(fileMap);
                          }}
                        />
                      )}

                      <div className="flex flex-col">
                        <Text variant="Caption1" className="text-[#4D4E58]">
                          {file.name}
                        </Text>
                        <Text variant="Caption2" className="text-[#86868B]">
                          {file.type.toUpperCase()}, {prettifyBytes(file.size)}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <LoadingIcon />
                </div>
              )}
            </div>

            <div className="sticky flex flex-col gap-2 p-6 bottom-0">
              {isSelectingFiles ? (
                <>
                  <ChangeProjectDropDown
                    showSelectedProject
                    projects={projects}
                    currentProject={currentProject}
                    onProjectChange={(projectId) => {
                      setCurrentProjectId(projectId);
                    }}
                    openDirection="top"
                  />
                  <Button
                    variant="primary"
                    onClick={onDownloadFileClicked}
                    disabled={totalFilesChecked === 0}
                  >
                    <ArrowPushDownIcon />{' '}
                    {totalFilesChecked > 1
                      ? intl?.formatMessage(
                          definedTranslation.previewFiles.downloadFilesBtn,
                        )
                      : intl?.formatMessage(
                          definedTranslation.previewFiles.downloadFileBtn,
                        )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={onCancelSelectFilesClicked}
                  >
                    {intl?.formatMessage(
                      definedTranslation.previewFiles.cancelBtn,
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <ChangeProjectDropDown
                    showSelectedProject={true}
                    projects={projects}
                    currentProject={currentProject}
                    onProjectChange={(projectId) => {
                      setCurrentProjectId(projectId);
                    }}
                    openDirection="top"
                  />

                  <Button
                    variant="primary"
                    onClick={onDownloadFileClicked}
                    disabled={selectedFileIndex === null}
                  >
                    <ArrowPushDownIcon />{' '}
                    {intl?.formatMessage(
                      definedTranslation.previewFiles.downloadFileBtn,
                    )}
                  </Button>
                  <Button variant="secondary" onClick={onSelectFilesClicked}>
                    {intl?.formatMessage(
                      definedTranslation.previewFiles.selectFilesBtn,
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-1 items-center justify-center px-6 pb-10 bg-[#F5F5F7] rounded-br-2xl">
            {PdfContent}
          </div>
        </div>

        <DialogFooter className="sm:hidden justify-between items-center">
          <ChangeProjectDropDown
            showSelectedProject={true}
            projects={projects}
            currentProject={currentProject}
            onProjectChange={(projectId) => {
              setCurrentProjectId(projectId);
            }}
            openDirection="top"
          />
          <Button
            variant="primary"
            onClick={onDownloadFileClicked}
            disabled={selectedFileIndex === null}
          >
            <ArrowPushDownIcon />
            {totalFilesChecked > 1
              ? intl?.formatMessage(
                  definedTranslation.previewFiles.downloadFilesBtn,
                )
              : intl?.formatMessage(
                  definedTranslation.previewFiles.downloadFileBtn,
                )}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogOverlay>
  );
};
